import React from 'react';
import moment from 'moment';
import {
  last,
  isEmpty,
  transform,
  isArray,
  setWith,
  compact,
  has,
  get,
  sortBy,
  pickBy,
  identity,
  each,
} from 'lodash';
import {
  CONTACT_ERRORS,
  CSV_LIST_FIELDS,
} from 'constants/contact';
import { DATE_FORMAT_CSV } from 'constants/config';
import { CONTACT_BASIC_FIELDS, CONTACT_OTHER_FIELDS } from '../constants/contact';


export const getFullName = contactObj => `${contactObj.first_name || ''} ${contactObj.last_name || ''}`;

export const getOutletList = outlets => (
  !isEmpty(outlets) ? last(sortBy(outlets, [outlet => outlet.created_date])).name : ''
);

export const getOutletNameList = outlets => outlets && outlets.map(o => o.name).join(', ');

export const getIndustryList = contactObj => (contactObj.industries ? contactObj.industries.join(', ') : '');

/**
 * Get the reply status based on various date fields.
 * The function returns the status and status code of the latest date.
 * If no status date is provided, it returns "Uncategorized" as the status.
 *
 * @param {Object} reply - An object containing potential date values for various reply statuses.
 * @returns {Object} An object with two properties: status (string) and statusCode (string).
 */
export const getReplyStatus = (reply =
{
  status_interested_date: null,
  status_not_interested_date: null,
  status_sponsorship_date: null,
  status_unsubscribed_date: null,
  status_not_now_date: null,
  status_auto_reply_date: null,
  status_bounced_date: null,
}) => {
  let dateFields = [
    reply.status_interested_date, reply.status_not_interested_date, reply.status_sponsorship_date,
    reply.status_unsubscribed_date, reply.status_not_now_date, reply.status_auto_reply_date,
    reply.status_bounced_date,
  ];
  // filter null values
  dateFields = dateFields.filter(x => x);
  // Find the latest (maximum) date
  const maxDate = dateFields.sort((right, left) => moment.utc(left).diff(moment.utc(right)))[0];
  let status;
  let statusCode;
  if (reply.status_interested_date && reply.status_interested_date === maxDate) {
    status = 'Interested';
    statusCode = 'interested';
  } else if (reply.status_not_interested_date && reply.status_not_interested_date === maxDate) {
    status = 'Not interested';
    statusCode = 'not_interested';
  } else if (reply.status_sponsorship_date && reply.status_sponsorship_date === maxDate) {
    status = 'Sponsorship';
    statusCode = 'sponsorship';
  } else if (reply.status_unsubscribed_date && reply.status_unsubscribed_date === maxDate) {
    status = 'Unsubscribed';
    statusCode = 'unsubscribed';
  } else if (reply.status_not_now_date && reply.status_not_now_date === maxDate) {
    status = 'Not now';
    statusCode = 'not_now';
  } else if (reply.status_auto_reply_date && reply.status_auto_reply_date === maxDate) {
    status = 'Auto reply';
    statusCode = 'auto_reply';
  } else if (reply.status_bounced_date && reply.status_bounced_date === maxDate) {
    status = 'Bounced';
    statusCode = 'bounced';
  } else {
    status = 'Uncategorized';
    statusCode = 'uncategorized';
  }
  return { status, statusCode };
};

export const getErrorText = (err, onlyText = false) => {
  switch (err.error_type) {
    case CONTACT_ERRORS.DUPLICATE:
      return onlyText ?
        `Duplicate contact, exists on sequence ${err.additional_data}`
        : (
          <React.Fragment>
            Duplicate contact, exists on sequence <span>{err.additional_data}</span>
          </React.Fragment>
        );
    case CONTACT_ERRORS.BLACKLISTED:
      return 'Contact is marked as "Do not contact".';
    case CONTACT_ERRORS.INVALID:
      return 'Email is invalid';
    case CONTACT_ERRORS.BOUNCED:
      return 'Contact has bounced.';
    case CONTACT_ERRORS.EMPTY_OUTLET_NAME:
      return 'Empty outlet name on CSV';
    case CONTACT_ERRORS.DUPLICATE_ON_SEQUENCE:
      return 'This contact already exists on this sequence.';
    // case CONTACT_ERRORS.VARIABLE_ERROR:
    //   return `Variable (${err.additional_data}) in Pitch is undefined for this contact`;
    case null:
    case undefined:
      return null;
    default:
      return err.error_type.startsWith('outlet.') ?
        `${err.error_type.substring(7)}: ${err.additional_data}` :
        `${err.additional_data}`;
  }
};

export const transformContactsErrorData = (contacts, sequence) => {
  const rows = [];
  for (let i = 0; i < contacts.length; i += 1) {
    const errors = sequence.allow_duplicates ?
      contacts[i].errors.filter(err => err.error_type !== CONTACT_ERRORS.DUPLICATE)
      : contacts[i].errors;

    if (isEmpty(errors)) {
      /* eslint-disable-next-line no-continue */
      continue;
    }

    const row = {
      first_name: contacts[i].first_name,
      last_name: contacts[i].last_name,
      email: contacts[i].email,
      title: contacts[i].title,
      industries: getIndustryList(contacts[i]),
      outlets: getOutletList(has(contacts[i], 'outlets') ? contacts[i].outlets.results : []),
      status_bounced_date: contacts[i].status_bounced_date,
      error: errors.map(e => getErrorText(e, true)).join(', '),
    };
    rows.push(row);
  }
  return rows;
};

export const jsonToCsv = (obj, opt) => {
  if (typeof obj !== 'object') {
    return null;
  }
  let option = opt || {};
  const scopechar = option.scopechar || '/';
  const delimeter = option.delimeter || ',';
  if (Array.isArray(obj) === false) option = [obj];
  let curs = [];
  let name = [];
  let rownum = [];
  let queue = [];
  const values = [];
  const rows = [];
  const headers = {};
  const headersArr = [];

  for (rownum = 0; rownum < obj.length; rownum += 1) {
    queue = [obj[rownum], ''];
    rows[rownum] = {};
    while (queue.length > 0) {
      curs = queue.shift();
      name = queue.shift();
      if (curs !== null && typeof curs === 'object') {
        const cursKeys = Object.keys(curs);
        for (let index = 0; index < cursKeys.length; index += 1) {
          const key = cursKeys[index];
          if (Object.prototype.hasOwnProperty.call(curs, key)) {
            queue.push(curs[key]);
            queue.push(name + (name ? scopechar : '') + key);
          }
        }
      } else {
        if (headers[name] === undefined) {
          headers[name] = true;
        }
        rows[rownum][name] = curs;
      }
    }
    values[rownum] = [];
  }

  // create csv text
  const headersKeys = Object.keys(headers);
  for (let index = 0; index < headersKeys.length; index += 1) {
    const key = headersKeys[index];
    if (Object.prototype.hasOwnProperty.call(headers, key)) {
      headersArr.push(key);
      for (rownum = 0; rownum < obj.length; rownum += 1) {
        values[rownum].push(rows[rownum][key] === undefined || rows[rownum][key] === null
          ? ''
          : JSON.stringify(rows[rownum][key]));
      }
    }
  }

  for (rownum = 0; rownum < obj.length; rownum += 1) {
    values[rownum] = values[rownum].join(delimeter);
  }
  return `${headersArr.join(delimeter)}\n${values.join('\n')}`;
};

export const createCsvFile = (filename, data) => {
  const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) { // IE 10+
    navigator.msSaveBlob(blob, filename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) { // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};

export const renderCsvValue = (key, value) => {
  if (CSV_LIST_FIELDS.includes(key)) {
    return compact(value.split(';'));
  }
  return isEmpty(value) ? null : value;
};

export const getErrorsCsv = (contacts, sequence) => {
  const filename = `errors-${moment().local().format(DATE_FORMAT_CSV)}.csv`;
  const jsonArray = transformContactsErrorData(contacts, sequence);
  const data = jsonToCsv(jsonArray);
  createCsvFile(filename, data);
};

export const transformContactsCSVObject = (contacts, mapping) => (
  contacts.map(contact => transform(contact, (r, v, k) => {
    if (isArray(mapping[k])) {
      setWith(r, mapping[k], renderCsvValue(mapping[k], v), Object);
    } else {
      // eslint-disable-next-line no-param-reassign
      r[mapping[k]] = renderCsvValue(mapping[k], v);
    }
  }))
);

export const transformContactsCustomField = contacts => (
  transform(contacts, (result, value) => {
    if (value.custom_field) {
      // eslint-disable-next-line no-param-reassign
      value.custom_field = Object.keys(value.custom_field).map(key => ({
        field_name: key,
        field_value: value.custom_field[key],
      }));

      // Don't send null custom_field value
      // eslint-disable-next-line no-param-reassign
      value.custom_field = value.custom_field.filter(customField => customField.field_value);

      if (isEmpty(value.custom_field)) {
        // eslint-disable-next-line no-param-reassign
        delete value.custom_field;
      }
    }
  })
);

export const transformContactCustomFieldToFormField = contact => ({
  ...contact,
  custom_field: transform(contact.custom_field, (result, value) => {
    // eslint-disable-next-line no-param-reassign
    result[value.field_name] = value.field_value;
  }, {}),
});

export const validContacts = contacts => contacts.filter(r => isEmpty(r.errors) && !isEmpty(r.id));

export const transformSequenceContacts = (previewContacts, sequenceId) => validContacts(previewContacts).map(c => ({
  contact: c.id,
  sequence: sequenceId,
  outlet: get(last(c.outlets), 'id'),
  ...!isEmpty(c.custom_field) ? { custom_fields: c.custom_field } : {},
}));

export const getMappingFromFormData = formData => transform(pickBy(formData, identity), (r, v, k) => {
  // eslint-disable-next-line no-param-reassign
  if (v.label === undefined && typeof v === 'object') {
    each(v, (value, key) => {
      if (value) {
        // eslint-disable-next-line no-param-reassign
        r[value.label] = [k, key];
      }
    });
  } else {
    // eslint-disable-next-line no-param-reassign
    r[v.label] = k;
  }
});

export const getContactBasicFields = fields => (
  isEmpty(fields) ? [] : CONTACT_BASIC_FIELDS.map(f => fields.find(field => field.mapping_field_in_db === f))
);

export const getContactAdvanceFields = fields => (
  isEmpty(fields) ? [] : CONTACT_OTHER_FIELDS.map(f => fields.find(field => field.mapping_field_in_db === f))
);

export const getContactCustomFields = fields => fields.filter(field => isEmpty(field.mapping_field_in_db));

export const getContactOtherFields = fields => getContactAdvanceFields(fields).concat(getContactCustomFields(fields));

export const getFieldName = csvField => (
  isEmpty(csvField.mapping_field_in_db) ? `custom_field.${csvField.name}` : csvField.mapping_field_in_db
);
